// ============================================================================
// ONGLET « QUALITÉ » — espace collaborateur (auto-évaluation, mobile-first).
//   Profil des donateurs recrutés : âge moyen + pyramide des âges, genre, et
//   qualité des bulletins (taux de validation). Un verdict synthétique répond en
//   1 coup d'œil : « Tu fais du bon travail ? ». Horizons : Mois / Trimestre /
//   Année / Depuis le début. Cartes Phase 2 (attrition, welcome call) en attente
//   des données Reporting par recruteur.
//   Charte Shariti (orange #EE722E, cartes crème) + recharts.
// ============================================================================
import { useState, useEffect, useCallback, useRef } from "react";
import { publicApi } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, PieChart, Pie,
  CartesianGrid, ReferenceLine, LineChart, Line,
} from "recharts";
import {
  Sparkles, ShieldCheck, Users, Award, AlertTriangle,
  HeartPulse, Cake, PhoneOff, TrendingDown, TrendingUp, CheckCircle2, ShieldAlert, Clock,
  Coins, ChevronLeft, ChevronRight, Layers,
} from "lucide-react";

// Horizons NAVIGABLES : jour / semaine / mois / trimestre / année / depuis le début.
// La navigation (◀ ▶) se fait via un « offset » entier (0 = période courante).
const HORIZONS = [
  ["day", "Jour"], ["week", "Semaine"], ["month", "Mois"],
  ["quarter", "Trimestre"], ["year", "Année"], ["all", "Depuis le début"],
];
// Horizons pour lesquels la navigation avant/arrière a du sens (pas « depuis le début »).
const NAVIGABLE = new Set(["day", "week", "month", "quarter", "year"]);

const TONE = {
  success: { text: "text-[#0F5A2F]", bg: "bg-[rgba(31,157,85,0.12)]", ring: "border-[rgba(31,157,85,0.35)]", bar: "rgba(31,157,85,0.9)" },
  info: { text: "text-[#204E9E]", bg: "bg-[rgba(47,111,237,0.12)]", ring: "border-[rgba(47,111,237,0.35)]", bar: "rgba(47,111,237,0.85)" },
  warning: { text: "text-[#8A5300]", bg: "bg-[rgba(224,138,0,0.14)]", ring: "border-[rgba(224,138,0,0.4)]", bar: "rgba(224,138,0,0.9)" },
  danger: { text: "text-[#8E241B]", bg: "bg-[rgba(192,57,43,0.12)]", ring: "border-[rgba(192,57,43,0.35)]", bar: "rgba(192,57,43,0.85)" },
};

// Genre : bleu (homme) / orange (femme) / gris (autre).
const GENDER_COLORS = { H: "rgba(47,111,237,0.85)", F: "rgba(238,114,46,0.85)", Autre: "rgba(24,24,24,0.35)" };
const QUALITY_META = [
  ["approuves", "Approuvés", "rgba(31,157,85,0.9)"],
  ["en_attente", "En attente", "rgba(24,24,24,0.25)"],
  ["invalides", "Annulés (welcome call)", "rgba(192,57,43,0.85)"],
  ["annules", "Annulés (autre)", "rgba(224,138,0,0.85)"],
];

const card = "rounded-[var(--radius)] border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)] p-4";
const fmtNum = (n) => (n == null ? "—" : String(n).replace(".", ","));

function ChartTip({ active, payload, suffix }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  return (
    <div className="rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-1.5 text-xs shadow-[var(--shadow-sm)]">
      <span className="font-semibold text-ink">{p.payload.label}</span>
      <span className="text-muted-ink"> · {p.value}{suffix || ""} {p.payload.pct != null ? `(${p.payload.pct}%)` : ""}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ Verdict */
function VerdictCard({ v }) {
  const t = TONE[v.tone] || TONE.info;
  return (
    <div className={`${card} reveal-item`} data-testid="quality-verdict-card">
      <div className="flex items-center gap-3">
        <div className={`grid h-12 w-12 shrink-0 place-items-center rounded-full ${t.bg} ${t.text}`}>
          <Award className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${t.ring} ${t.bg} ${t.text}`} data-testid="quality-verdict-label">{v.label}</span>
            <span className="text-2xl font-semibold leading-none text-ink" data-testid="quality-verdict-score">{v.score}<span className="text-sm text-muted-ink">/100</span></span>
          </div>
          <p className="mt-1 text-sm font-semibold text-ink">{v.phrase}</p>
        </div>
      </div>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-[var(--surface-2)]" aria-hidden="true">
        <div className="h-full rounded-full" style={{ width: `${Math.max(3, v.score)}%`, background: t.bar, transition: "width 500ms ease" }} />
      </div>
      <ul className="mt-3 space-y-1">
        {(v.proofs || []).map((p, i) => (
          <li key={i} className="flex items-center gap-1.5 text-xs text-muted-ink">
            <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: t.bar }} />{p}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11px] text-muted-ink" data-testid="quality-verdict-explain">Basé sur tes bulletins et le profil des donateurs (horizon sélectionné).</p>
    </div>
  );
}

/* --------------------------------------------------------------- Âge moyen */
function AverageAgeCard({ value, n }) {
  return (
    <div className={`${card} reveal-item`} data-testid="quality-average-age-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-ink">Âge moyen</p>
          <div className="mt-1 flex items-end gap-1">
            <span className="text-4xl font-semibold leading-none text-ink" data-testid="quality-average-age-value">{fmtNum(value)}</span>
            <span className="mb-0.5 text-sm text-muted-ink">ans</span>
          </div>
          <p className="mt-1 text-xs text-muted-ink">Sur {n} donateur{n > 1 ? "s" : ""} avec âge connu</p>
        </div>
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[var(--surface-2)]"><Cake className="h-5 w-5 text-[var(--shariti-orange)]" /></div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------- Don moyen */
function DonMoyenCard({ dm }) {
  const avg = dm?.average;
  const n = dm?.count || 0;
  return (
    <div className={`${card} reveal-item`} data-testid="quality-don-moyen-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-ink">Don moyen</p>
          <div className="mt-1 flex items-end gap-1">
            <span className="text-4xl font-semibold leading-none text-ink" data-testid="quality-don-moyen-value">{avg == null ? "—" : fmtNum(avg)}</span>
            <span className="mb-0.5 text-sm text-muted-ink">€ / mois</span>
          </div>
          <p className="mt-1 text-xs text-muted-ink">Sur {n} don{n > 1 ? "s" : ""} comptabilisé{n > 1 ? "s" : ""}</p>
        </div>
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[var(--surface-2)]"><Coins className="h-5 w-5 text-[var(--shariti-orange)]" /></div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------- Évolution 12 mois */
const EVO_METRICS = [
  { key: "don_moyen", label: "Don moyen", pct: false, color: "rgba(238,114,46,0.95)", unit: " €" },
  { key: "mature_share", label: "+ de 25 ans", pct: true, color: "rgba(31,157,85,0.9)", unit: "%" },
  { key: "validation_rate", label: "Validation", pct: true, color: "rgba(47,111,237,0.85)", unit: "%" },
  { key: "wc_rate", label: "Annul. welcome", pct: true, color: "rgba(192,57,43,0.85)", unit: "%" },
];

function EvoTip({ active, payload, meta }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  const v = p._val;
  const shown = v == null ? "—" : meta.pct ? `${v}%` : `${fmtNum(v)} €`;
  return (
    <div className="rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-1.5 text-xs shadow-[var(--shadow-sm)]">
      <div className="font-semibold text-ink">{p.label}</div>
      <div className="text-muted-ink">{meta.label} : <b className="text-ink">{shown}</b></div>
      <div className="text-[11px] text-muted-ink">{p.bulletins} bulletin{p.bulletins > 1 ? "s" : ""}</div>
    </div>
  );
}

function EvolutionCard({ series }) {
  const [mkey, setMkey] = useState("don_moyen");
  const meta = EVO_METRICS.find((m) => m.key === mkey) || EVO_METRICS[0];
  const data = (series || []).map((s) => ({
    ...s,
    _val: s[mkey] == null ? null : (meta.pct ? Math.round(s[mkey] * 1000) / 10 : s[mkey]),
  }));
  const hasAny = data.some((d) => d._val != null);
  return (
    <div className={`${card} reveal-item`} data-testid="quality-evolution-card">
      <div className="mb-2 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-[var(--shariti-orange)]" />
        <h3 className="text-base font-semibold text-ink">Évolution sur 12 mois</h3>
      </div>
      <div className="mb-2 flex flex-wrap gap-1.5" data-testid="quality-evolution-metrics">
        {EVO_METRICS.map((m) => (
          <button key={m.key} onClick={() => setMkey(m.key)} data-testid={`quality-evolution-metric-${m.key}`}
            className={`rounded-full border px-2.5 py-1 text-[11px] font-bold transition-colors active:scale-[0.98] ${mkey === m.key ? "border-transparent bg-[var(--shariti-orange)] text-white" : "border-[var(--border-soft)] bg-white text-muted-ink"}`}>
            {m.label}
          </button>
        ))}
      </div>
      {!hasAny ? (
        <p className="py-8 text-center text-xs text-muted-ink" data-testid="quality-evolution-empty">Pas encore assez d'historique pour tracer une tendance.</p>
      ) : (
        <div style={{ width: "100%", height: 220 }} data-testid="quality-evolution-chart">
          <ResponsiveContainer width="100%" height={220} minWidth={0}>
            <LineChart data={data} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(24,24,24,0.06)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "rgba(24,24,24,0.5)" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: "rgba(24,24,24,0.5)" }} axisLine={false} tickLine={false} width={42} domain={meta.pct ? [0, 100] : ["auto", "auto"]} tickFormatter={(v) => (meta.pct ? `${v}%` : v)} />
              <Tooltip cursor={{ stroke: "rgba(24,24,24,0.12)" }} content={<EvoTip meta={meta} />} />
              <Line type="monotone" dataKey="_val" stroke={meta.color} strokeWidth={2.5} dot={{ r: 2.5, fill: meta.color }} activeDot={{ r: 4 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <p className="mt-1 text-center text-[11px] text-muted-ink">{meta.label} par mois — sur les 12 derniers mois.</p>
    </div>
  );
}

/* ---------------------------------------------------------- Pyramide âges */
const AGE_MATURE = "rgba(238,114,46,0.9)";   // 26+ : profil recherché
const AGE_YOUNG = "rgba(24,24,24,0.22)";     // 18–25 : à limiter (risque d'attrition)

function AgePyramidCard({ rows }) {
  const total = rows.reduce((s, r) => s + r.value, 0);
  return (
    <div className={`${card} reveal-item`} data-testid="quality-age-pyramid-card">
      <div className="mb-2 flex items-center gap-2">
        <Users className="h-4 w-4 text-[var(--shariti-orange)]" />
        <h3 className="text-base font-semibold text-ink">Pyramide des âges</h3>
      </div>
      <p className="mb-2 text-xs text-muted-ink">Objectif : une <b className="text-ink">majorité de donateurs de plus de 25 ans</b>. Trop de 18–25 ans = signal à surveiller.</p>
      <div data-testid="quality-age-pyramid-chart" style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer width="100%" height={220} minWidth={0}>
          <BarChart layout="vertical" data={rows} margin={{ top: 4, right: 30, left: 4, bottom: 0 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="label" width={52} axisLine={false} tickLine={false} tick={{ fill: "rgba(24,24,24,0.56)", fontSize: 12 }} />
            <Tooltip cursor={{ fill: "rgba(24,24,24,0.04)" }} content={<ChartTip />} />
            <Bar dataKey="value" radius={[8, 8, 8, 8]} label={{ position: "right", fill: "rgba(24,24,24,0.56)", fontSize: 11, formatter: (v) => (v > 0 && total ? `${Math.round((v / total) * 100)}%` : "") }}>
              {rows.map((r) => <Cell key={r.label} fill={r.mature ? AGE_MATURE : AGE_YOUNG} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-1 flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11px] text-muted-ink">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: AGE_MATURE }} />+ de 25 ans (recherché)</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: AGE_YOUNG }} />18–25 ans (à limiter)</span>
      </div>
    </div>
  );
}

/* ----------------------------------------------------- Welcome call (annulation) */
function WelcomeCallCard({ wc }) {
  const rate = Math.round((wc.cancellation_rate || 0) * 100);
  const target = Math.round((wc.target || 0.15) * 100);
  const t = { good: TONE.success, warn: TONE.warning, bad: TONE.danger }[wc.status] || TONE.warning;
  const msg = wc.status === "good" ? "Sous l'objectif — bien joué"
    : (wc.status === "warn" ? "Au-dessus de l'objectif" : "Trop élevé — à corriger");
  return (
    <div className={`${card} reveal-item`} data-testid="quality-welcome-call-card">
      <div className="mb-2 flex items-center gap-2">
        <PhoneOff className="h-4 w-4 text-[var(--shariti-orange)]" />
        <h3 className="text-base font-semibold text-ink">Annulation au welcome call</h3>
      </div>
      <p className="mb-2 text-xs text-muted-ink">Part de tes bulletins annulés lors de l'appel de bienvenue (passés en invalide).</p>
      <div className="flex items-end justify-between gap-3">
        <span className={`text-4xl font-semibold leading-none ${t.text}`} data-testid="quality-welcome-call-rate">{rate}%</span>
        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${t.ring} ${t.bg} ${t.text}`} data-testid="quality-welcome-call-status">{msg}</span>
      </div>
      <div className="relative mt-3 h-2.5 w-full overflow-hidden rounded-full bg-[var(--surface-2)]">
        <div className="h-full rounded-full" style={{ width: `${Math.min(100, rate)}%`, background: t.bar, transition: "width 500ms ease" }} />
        <div className="absolute top-[-3px] h-[16px] w-[2px] bg-[rgba(24,24,24,0.55)]" style={{ left: `${target}%` }} title={`Objectif ${target}%`} />
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-muted-ink">
        <span>{wc.annules} annulés / {wc.total} bulletins</span>
        <span>Objectif ≤ {target}%</span>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- Genre */
function GenderCard({ rows }) {
  const total = rows.reduce((s, r) => s + r.value, 0);
  const dominant = [...rows].sort((a, b) => b.value - a.value)[0];
  const pieData = rows.filter((r) => r.value > 0);
  return (
    <div className={`${card} reveal-item`} data-testid="quality-gender-card">
      <div className="mb-2 flex items-center gap-2">
        <Users className="h-4 w-4 text-[var(--shariti-orange)]" />
        <h3 className="text-base font-semibold text-ink">Genre des donateurs</h3>
      </div>
      <div className="relative" data-testid="quality-gender-donut" style={{ width: "100%", height: 200 }}>
        <ResponsiveContainer width="100%" height={200} minWidth={0}>
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="label" innerRadius={58} outerRadius={84} paddingAngle={2} stroke="none">
              {pieData.map((r) => <Cell key={r.key} fill={GENDER_COLORS[r.key] || GENDER_COLORS.Autre} />)}
            </Pie>
            <Tooltip content={<ChartTip />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-semibold leading-none text-ink">{dominant && total ? `${dominant.pct}%` : "—"}</span>
          <span className="text-[11px] text-muted-ink">{dominant && total ? dominant.label.toLowerCase() : "genre"}</span>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1">
        {rows.map((r) => (
          <span key={r.key} className="inline-flex items-center gap-1.5 text-xs text-muted-ink">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: GENDER_COLORS[r.key] }} />
            {r.label} · <b className="text-ink">{r.value}</b> ({r.pct}%)
          </span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------- Répartition bulletins */
// NB : carte volontairement NEUTRE. La métrique mise en avant est le TAUX D'ANNULATION
// au welcome call (colorée selon l'objectif ≤ 15 %), et non le taux de validation —
// pour ne pas donner l'illusion d'une « bonne qualité » quand les annulations sont trop élevées.
function BulletinQualityCard({ q }) {
  const total = q.total || 0;
  const seg = QUALITY_META.map(([key, label, color]) => ({ key, label, color, value: q[key] || 0 }))
    .filter((s) => s.value > 0);
  const invalides = q.invalides || 0;
  const annulRate = total > 0 ? invalides / total : 0;
  const annulPct = Math.round(annulRate * 100);
  const target = 15;
  const t = annulRate <= 0.15 ? TONE.success : (annulRate <= 0.20 ? TONE.warning : TONE.danger);
  const verdict = annulRate <= 0.15 ? "Validation saine" : (annulRate <= 0.20 ? "À surveiller" : "Trop d'annulations");
  const validationPct = Math.round((q.validation_rate || 0) * 100);
  return (
    <div className={`${card} reveal-item`} data-testid="quality-bulletins-card">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-[var(--shariti-orange)]" />
          <h3 className="text-base font-semibold text-ink">Répartition des bulletins</h3>
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-bold ${t.ring} ${t.bg} ${t.text}`} data-testid="quality-bulletins-verdict">{verdict}</span>
      </div>
      <p className="mb-2 text-xs text-muted-ink">Ce que deviennent tes bulletins après l'appel de bienvenue.</p>

      {/* Barre 100% empilée (répartition factuelle) */}
      <div data-testid="quality-bulletins-stacked-bar" className="flex h-4 w-full overflow-hidden rounded-full bg-[var(--surface-2)]">
        {total > 0 ? seg.map((s) => (
          <div key={s.key} title={`${s.label} : ${s.value}`} style={{ width: `${(s.value / total) * 100}%`, background: s.color }} />
        )) : null}
      </div>

      {/* Métrique MISE EN AVANT = taux d'annulation welcome call (colorée selon l'objectif). */}
      <div className="mt-3 flex items-end justify-between gap-3">
        <div>
          <div className={`text-3xl font-semibold leading-none ${t.text}`} data-testid="quality-bulletins-cancel-rate">{annulPct}%</div>
          <p className="mt-1 text-xs text-muted-ink">Annulés au welcome call ({invalides}/{total}) · objectif ≤ {target}%</p>
        </div>
        <span className="shrink-0 text-right text-[11px] text-muted-ink">Taux de validation<br /><b className="text-ink" data-testid="quality-bulletins-validation-rate">{validationPct}%</b></span>
      </div>

      <ul className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1">
        {QUALITY_META.map(([key, label, color]) => (
          <li key={key} className="flex items-center gap-1.5 text-xs text-muted-ink">
            <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color }} />
            {label} · <b className="text-ink">{q[key] || 0}</b>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ----------------------------------------------------------- Attrition (Phase 2) */
// Jauge « feu tricolore » : zone verte (objectif atteint) à gauche, rouge (au-dessus) à droite.
const ZONE_GOOD = "rgba(31,157,85,0.22)";
const ZONE_BAD = "rgba(192,57,43,0.18)";
const ZONE_NEUTRAL_L = "rgba(24,24,24,0.10)";
const ZONE_NEUTRAL_R = "rgba(24,24,24,0.05)";
// Histogramme mois par mois.
const BAR_GOOD = "rgba(31,157,85,0.9)";
const BAR_BAD = "rgba(192,57,43,0.85)";
const NEUTRAL = { text: "text-muted-ink", bg: "bg-[var(--surface-2)]", ring: "border-[var(--border-soft)]", bar: "rgba(24,24,24,0.4)" };
const BANNER_ICON = { success: CheckCircle2, warning: AlertTriangle, danger: ShieldAlert, neutral: Clock };
const trendGlyph = (dir) => (dir === "down" ? "↘" : dir === "up" ? "↗" : "→");

/* Bannière-verdict : le collaborateur sait en 1 phrase s'il fait du bon travail. */
function AttrHeadline({ h }) {
  const tone = h?.tone || "neutral";
  const t = tone === "neutral" ? NEUTRAL : (TONE[tone] || TONE.info);
  const Icon = BANNER_ICON[tone] || Clock;
  return (
    <div className={`flex items-start gap-3 rounded-[var(--radius)] border ${t.ring} ${t.bg} p-3`} data-testid="quality-attrition-headline">
      <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white/70 ${t.text}`}><Icon className="h-5 w-5" /></div>
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-bold ${t.text}`} data-testid="quality-attrition-headline-title">{h?.title}</p>
        <p className="mt-0.5 text-xs text-muted-ink">{h?.subtitle}</p>
      </div>
    </div>
  );
}

/* Jauge : objectif au centre. Curseur à gauche (vert) = bon · à droite (rouge) = mauvais. */
function AttrMeter({ label, hint, rate, target, status, trend, reliable, testid }) {
  const pct = rate == null ? null : Math.round(rate * 1000) / 10;
  const targetPct = Math.round((target || 0) * 100);
  const max = (targetPct * 2) || 100;                       // objectif placé au centre (50%)
  const needle = pct == null ? 0 : Math.min(100, (pct / max) * 100);
  const t = !reliable ? NEUTRAL : ({ good: TONE.success, warn: TONE.warning, bad: TONE.danger }[status] || TONE.warning);
  const chip = !reliable ? "À confirmer" : (status === "good" ? "Dans les clous" : status === "warn" ? "Proche de la limite" : "Au-dessus");
  const goodZone = reliable ? ZONE_GOOD : ZONE_NEUTRAL_L;
  const badZone = reliable ? ZONE_BAD : ZONE_NEUTRAL_R;
  const showTrend = reliable && trend && trend.delta_pts != null && trend.dir !== "flat";
  const trTone = trend ? (trend.tone === "success" ? TONE.success : trend.tone === "danger" ? TONE.danger : NEUTRAL) : NEUTRAL;
  return (
    <div className="rounded-[var(--radius)] border border-[var(--border-soft)] bg-white p-3" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-ink">{label}</p>
          <p className="text-[11px] text-muted-ink">{hint}</p>
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold ${t.ring} ${t.bg} ${t.text}`} data-testid={`${testid}-status`}>{chip}</span>
      </div>
      <div className="mt-2 flex items-end gap-2">
        <span className={`text-3xl font-semibold leading-none ${t.text}`} data-testid={`${testid}-rate`}>{pct == null ? "—" : `${fmtNum(pct)}%`}</span>
        {showTrend && (
          <span className={`mb-0.5 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${trTone.bg} ${trTone.text}`} title="vs le mois précédent">
            {trendGlyph(trend.dir)} {fmtNum(Math.abs(trend.delta_pts))} pts
          </span>
        )}
      </div>
      <div className="relative mt-2 h-3.5 w-full overflow-hidden rounded-full" aria-hidden="true">
        <div className="absolute inset-0 flex">
          <div style={{ width: "50%", background: goodZone }} />
          <div style={{ width: "50%", background: badZone }} />
        </div>
        {pct != null && (
          <div className="absolute top-[-2px] h-[18px] w-[3px] rounded-full" style={{ left: `calc(${needle}% - 1.5px)`, background: t.bar, transition: "left 500ms ease" }} title={`${fmtNum(pct)}%`} />
        )}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-muted-ink">
        <span>0%</span>
        <span className="font-semibold">objectif ≤ {targetPct}%</span>
        <span>{max}%+</span>
      </div>
    </div>
  );
}

function AttrBarTip({ active, payload, objective, unit = "livrés" }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  const ok = p.pct != null && p.pct <= objective;
  return (
    <div className="rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-1.5 text-xs shadow-[var(--shadow-sm)]">
      <div className="font-semibold text-ink">{p.label}</div>
      <div className="text-muted-ink">Attrition : <b className="text-ink">{p.pct == null ? "—" : `${fmtNum(p.pct)}%`}</b></div>
      <div className={`mt-0.5 text-[11px] font-semibold ${ok ? "text-[#0F5A2F]" : "text-[#8E241B]"}`}>{ok ? "Objectif atteint" : "Au-dessus de l'objectif"}</div>
      {p.delta != null && (
        <div className={`mt-0.5 text-[11px] font-semibold ${p.delta <= 0 ? "text-[#0F5A2F]" : "text-[#8E241B]"}`}>
          {p.delta <= 0 ? "↘" : "↗"} {fmtNum(Math.abs(p.delta))} pts vs mois préc.
        </div>
      )}
      <div className="mt-0.5 text-[11px] text-muted-ink">{p.nb_resiliations} résiliés / {p.nb_livres} {unit}</div>
    </div>
  );
}

function AttritionCard({ a }) {
  const hasData = !!(a && a.has_data);
  const worse = (a?.summary_3m?.status === "bad" || (a?.summary_3m?.status === "warn" && a?.summary_12m?.status !== "bad")) ? "3m" : "12m";
  const [metric, setMetric] = useState(worse);
  useEffect(() => { setMetric(worse); }, [worse]);

  if (!hasData) {
    const reason = a?.reason;
    const src = a?.source_label || a?.source_report_date;
    const txt = reason === "not_configured"
      ? "Connexion au Reporting non configurée sur cet environnement."
      : (reason === "auth" || reason === "unavailable")
        ? "Données d'attrition momentanément indisponibles. Réessaie plus tard."
        : reason === "no_cohort"
          ? `Aucun donateur recruté sur cette période dans le dernier rapport${src ? ` (à jour au ${src})` : ""}. Choisis un horizon plus large (ex. « Depuis le début »).`
          : "Pas encore de rapport d'attrition pour toi. Cet indicateur apparaîtra dès le prochain rapport.";
    return (
      <div className="rounded-[var(--radius)] border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)]/60 p-4" data-testid="quality-attrition-card">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white/70 text-[var(--shariti-orange)]"><HeartPulse className="h-5 w-5" /></div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-ink">Attrition des donateurs</h3>
            <p className="mt-0.5 text-xs text-muted-ink" data-testid="quality-attrition-empty">{txt}</p>
          </div>
        </div>
      </div>
    );
  }

  const s12 = a.summary_12m || {};
  const s3 = a.summary_3m || {};
  const reliable = !!a.reliable;
  const isCohort = a.basis === "recrutement";
  const unit = isCohort ? "recrutés" : "livrés";
  const objective = metric === "3m" ? 11 : 30;
  const alerts = isCohort ? (a.cohort_alerts || []) : [];
  const chartData = (a.series || []).map((s) => {
    const frac = metric === "3m" ? s.taux_pre_attrition : s.taux_attrition;
    return {
      label: s.label,
      pct: frac == null ? null : Math.round(frac * 1000) / 10,
      nb_livres: s.nb_livres,
      nb_resiliations: metric === "3m" ? s.nb_resiliations_precoces : s.nb_resiliations,
      // Comparatif cohortes : delta (pts) vs mois précédent + maturité pour l'indicateur courant.
      delta: metric === "3m" ? s.delta_pre_pts : s.delta_attr_pts,
      mature: metric === "3m" ? s.mature_3m : s.mature_12m,
    };
  });
  // Cohortes assez mûres pour l'indicateur sélectionné (base du comparatif).
  const matureRows = isCohort ? chartData.filter((d) => d.mature && d.pct != null) : [];

  return (
    <div className={`${card} reveal-item`} data-testid="quality-attrition-card">
      <div className="mb-2 flex items-center gap-2">
        <HeartPulse className="h-4 w-4 text-[var(--shariti-orange)]" />
        <h3 className="text-base font-semibold text-ink">Attrition des donateurs</h3>
        <span className="ml-auto rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[10px] font-bold text-muted-ink">moins = mieux</span>
      </div>

      <AttrHeadline h={a.headline} />

      {isCohort && a.note && (
        <p className="mt-1.5 text-[11px] leading-snug text-muted-ink" data-testid="quality-attrition-note">{a.note}</p>
      )}

      {alerts.length > 0 && (
        <div className="mt-2 rounded-[var(--radius)] border border-[rgba(192,57,43,0.35)] bg-[rgba(192,57,43,0.08)] p-3" data-testid="quality-attrition-alerts">
          <div className="flex items-center gap-1.5 text-xs font-bold text-[#8E241B]">
            <ShieldAlert className="h-4 w-4" />Cohortes à risque ({alerts.length})
          </div>
          <ul className="mt-1 space-y-0.5">
            {alerts.slice(0, 3).map((c, i) => (
              <li key={i} className="text-[11px] leading-snug text-[#8E241B]" data-testid="quality-attrition-alert-item">
                <b>{c.label}</b> · {c.metric === "3m" ? "pré-attrition" : "attrition"} <b>{Math.round(c.rate * 100)}%</b> (cible ≤ {Math.round(c.target * 100)}%) · {c.nb_livres} recrutés
              </li>
            ))}
          </ul>
          {alerts.length > 3 && <p className="mt-0.5 text-[10px] text-[#8E241B]/80">+ {alerts.length - 3} autre(s) cohorte(s) au-dessus de l'objectif.</p>}
        </div>
      )}

      <div className="mt-2 grid grid-cols-1 gap-2">
        <AttrMeter testid="quality-attrition-12m" label="Attrition — 12 mois" hint={`Résiliations / ${unit}`}
          rate={s12.rate} target={s12.target != null ? s12.target : 0.30} status={s12.status} trend={s12.trend} reliable={reliable} />
        <AttrMeter testid="quality-attrition-3m" label="Pré-attrition — 3 mois" hint="Résiliations dans les 3 premiers mois"
          rate={s3.rate} target={s3.target != null ? s3.target : 0.11} status={s3.status} trend={s3.trend} reliable={reliable} />
      </div>

      {reliable && chartData.length > 0 && (
        <div className="mt-3">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-ink"><TrendingDown className="h-3.5 w-3.5" />{isCohort ? "Par mois de recrutement" : "Mois par mois"}</div>
            <div className="flex items-center gap-1 rounded-full border border-[var(--border-soft)] bg-white p-0.5" data-testid="quality-attrition-metric-toggle">
              {[["3m", "3 mois"], ["12m", "12 mois"]].map(([k, l]) => (
                <button key={k} onClick={() => setMetric(k)} data-testid={`quality-attrition-metric-${k}`}
                  className={`rounded-full px-2 py-1 text-[11px] font-bold transition-colors ${metric === k ? "bg-[var(--surface-2)] text-ink" : "text-muted-ink"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div data-testid="quality-attrition-chart" style={{ width: "100%", height: 190 }}>
            <ResponsiveContainer width="100%" height={190} minWidth={0}>
              <BarChart data={chartData} margin={{ top: 6, right: 14, left: 2, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(24,24,24,0.06)" vertical={false} />
                <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: "rgba(24,24,24,0.56)", fontSize: 11 }} />
                <YAxis domain={[0, (dataMax) => Math.max(Math.ceil((objective * 1.6) / 5) * 5, Math.ceil((dataMax + 4) / 5) * 5)]} tickFormatter={(v) => `${v}%`} axisLine={false} tickLine={false} tick={{ fill: "rgba(24,24,24,0.56)", fontSize: 11 }} width={36} />
                <Tooltip cursor={{ fill: "rgba(24,24,24,0.04)" }} content={<AttrBarTip objective={objective} unit={unit} />} />
                <ReferenceLine y={objective} stroke="rgba(24,24,24,0.5)" strokeDasharray="4 4" label={{ value: `objectif ${objective}%`, position: "insideTopRight", fill: "rgba(24,24,24,0.56)", fontSize: 10 }} />
                <Bar dataKey="pct" radius={[6, 6, 0, 0]} maxBarSize={46}>
                  {chartData.map((d, i) => <Cell key={i} fill={d.pct != null && d.pct <= objective ? BAR_GOOD : BAR_BAD} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-1 flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11px] text-muted-ink">
            <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: BAR_GOOD }} />Objectif atteint</span>
            <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: BAR_BAD }} />Au-dessus de l'objectif</span>
          </div>

          {isCohort && (
            <div className="mt-2 rounded-[var(--radius)] border border-[var(--border-soft)] bg-[var(--surface-2)]/50 p-2" data-testid="quality-cohort-compare">
              <div className="mb-1 text-[11px] font-semibold text-muted-ink">Comparatif des cohortes — écart vs mois précédent ({metric === "3m" ? "3 mois" : "12 mois"})</div>
              {matureRows.length === 0 ? (
                <p className="text-[11px] text-muted-ink" data-testid="quality-cohort-compare-empty">Cohortes encore trop récentes pour être comparées sur {metric === "3m" ? "3 mois" : "12 mois"}.</p>
              ) : (
                <div className="flex gap-1.5 overflow-x-auto pb-1">
                  {matureRows.map((d, i) => (
                    <div key={i} className="min-w-[64px] shrink-0 rounded-lg border border-[var(--border-soft)] bg-white px-2 py-1 text-center" data-testid="quality-cohort-compare-item">
                      <div className="text-[10px] font-semibold text-ink">{d.label}</div>
                      <div className="text-xs font-bold" style={{ color: d.pct <= objective ? "#0F5A2F" : "#8E241B" }}>{fmtNum(d.pct)}%</div>
                      {d.delta != null ? (
                        <div className={`text-[10px] font-bold ${d.delta <= 0 ? "text-[#0F5A2F]" : "text-[#8E241B]"}`}>{d.delta <= 0 ? "↘" : "↗"} {fmtNum(Math.abs(d.delta))} pts</div>
                      ) : (
                        <div className="text-[10px] text-muted-ink">réf.</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-2 flex flex-wrap justify-between gap-2 text-[11px] text-muted-ink">
        <span data-testid="quality-attrition-volumes">{s12.nb_resiliations} résiliations / {s12.nb_livres} {unit}</span>
        {a.last_report && <span>{isCohort ? "À jour au" : "Dernier rapport :"} {a.last_report}</span>}
      </div>
    </div>
  );
}

/* ================================================================ MAIN */
export function CollabQualite({ token, qualiteUrl }) {
  const [period, setPeriod] = useState("month");
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const changePeriod = (p) => { setPeriod(p); setOffset(0); };
  // URL de base configurable : espace collaborateur (public) OU espace responsable (resp).
  const baseUrl = qualiteUrl || `/pointage/public/${token}/qualite`;

  // Garde anti « condition de course » : en cas de changements rapides d'horizon,
  // seule la réponse de la DERNIÈRE requête est appliquée (les réponses en retard
  // sont ignorées pour ne pas écraser les bonnes données avec des données périmées).
  const reqIdRef = useRef(0);
  const load = useCallback(async () => {
    const myReq = ++reqIdRef.current;
    setLoading(true); setError(false);
    try {
      const sep = baseUrl.includes("?") ? "&" : "?";
      const r = await publicApi.get(`${baseUrl}${sep}period=${period}&offset=${offset}`);
      if (myReq !== reqIdRef.current) return;   // réponse périmée → on l'ignore
      setData(r.data);
    } catch {
      if (myReq !== reqIdRef.current) return;
      setError(true);
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, [baseUrl, period, offset]);
  useEffect(() => { load(); }, [load]);

  const canNav = NAVIGABLE.has(period);
  const canForward = !!data?.can_forward;
  const evolution = data?.evolution || [];
  const hasEvolution = evolution.some((s) => s && (s.don_moyen != null || s.mature_share != null || s.validation_rate != null));

  return (
    <div className="space-y-3" data-testid="quality-tab">
      {/* Header + sélecteur d'horizon + navigation (sticky) */}
      <div className="sticky top-0 z-20 -mx-4 border-b border-[var(--border-soft)] bg-[var(--bg-subtle)] px-4 pb-2 pt-1 shadow-[var(--shadow-sm)]">
        <div className="mb-2 min-w-0">
          <h2 className="text-lg font-semibold tracking-tight text-ink">Qualité</h2>
          <p className="text-xs text-muted-ink">Ton travail, lisible en 1 coup d'œil</p>
        </div>
        <div className="flex items-center gap-1 overflow-x-auto rounded-full border border-[var(--border-soft)] bg-white p-1" data-testid="quality-horizon">
          {HORIZONS.map(([p, l]) => (
            <button key={p} onClick={() => changePeriod(p)} data-testid={`quality-horizon-${p}`}
              className={`shrink-0 whitespace-nowrap rounded-full px-3 py-2 text-xs font-bold transition-colors active:scale-[0.98] ${period === p ? "bg-[var(--surface-2)] text-[var(--shariti-ink)] shadow-[var(--shadow-sm)]" : "text-muted-ink"}`}>{l}</button>
          ))}
        </div>
        {/* Barre de période : navigation ◀ ▶ + libellé (avec jour de la semaine) */}
        <div className="mt-2 flex items-center justify-center gap-2" data-testid="quality-period-bar">
          {canNav && (
            <button onClick={() => setOffset((o) => o - 1)} data-testid="quality-nav-prev" aria-label="Période précédente"
              className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-[var(--border-soft)] bg-white text-muted-ink transition-colors hover:text-ink active:scale-95">
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          <span className="truncate text-center text-xs font-semibold text-muted-ink" data-testid="quality-period-label">{data?.label || "…"}</span>
          {canNav && (
            <button onClick={() => setOffset((o) => Math.min(0, o + 1))} disabled={!canForward} data-testid="quality-nav-next" aria-label="Période suivante"
              className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-[var(--border-soft)] bg-white text-muted-ink transition-colors hover:text-ink active:scale-95 disabled:cursor-not-allowed disabled:opacity-40">
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
        {offset !== 0 && (
          <div className="mt-1 flex justify-center">
            <button onClick={() => setOffset(0)} data-testid="quality-nav-now"
              className="text-[11px] font-semibold text-[var(--shariti-orange)] underline-offset-2 hover:underline">Revenir à la période actuelle</button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="space-y-3" data-testid="quality-loading-state">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-32 w-full rounded-[var(--radius)]" />)}
        </div>
      ) : error ? (
        <div className={`${card} flex flex-col items-center gap-2 text-center`} data-testid="quality-error-state">
          <AlertTriangle className="h-6 w-6 text-[rgba(192,57,43,0.85)]" />
          <p className="text-sm font-semibold text-ink">Impossible de charger tes indicateurs</p>
          <Button onClick={load} className="mt-1 bg-[var(--shariti-orange)] hover:bg-[#d9631f]" data-testid="quality-error-retry-button">Réessayer</Button>
        </div>
      ) : data && data.has_perso === false ? (
        <div className={`${card} flex flex-col items-center gap-2 text-center`} data-testid="quality-no-perso-state">
          <Sparkles className="h-6 w-6 text-[var(--shariti-orange)]" />
          <p className="text-sm font-semibold text-ink">Pas de production personnelle</p>
          <p className="text-xs text-muted-ink">Cet espace n'est pas rattaché à une fiche collaborateur : il n'y a pas de bulletins à évaluer ici.</p>
        </div>
      ) : !data?.has_data ? (
        <>
          <div className={`${card} flex flex-col items-center gap-2 text-center`} data-testid="quality-empty-state">
            <Sparkles className="h-6 w-6 text-[var(--shariti-orange)]" />
            <p className="text-sm font-semibold text-ink">Pas encore de données sur « {data?.label} »</p>
            <p className="text-xs text-muted-ink">Essaie un autre horizon ou navigue vers une autre période.</p>
            <div className="mt-1 flex flex-wrap justify-center gap-2">
              {offset !== 0 && <Button variant="outline" onClick={() => setOffset(0)} data-testid="quality-empty-now-cta">Période actuelle</Button>}
              {period !== "all" && <Button variant="outline" onClick={() => changePeriod("all")} data-testid="quality-empty-state-cta">Voir depuis le début</Button>}
            </div>
          </div>
          {hasEvolution && <EvolutionCard series={evolution} />}
        </>
      ) : (
        <>
          <VerdictCard v={data.verdict} />
          <WelcomeCallCard wc={data.welcome_call || {}} />
          <AverageAgeCard value={data.average_age} n={data.totals?.avec_age || 0} />
          <DonMoyenCard dm={data.don_moyen || {}} />
          <AgePyramidCard rows={data.age_pyramid || []} />
          <GenderCard rows={data.gender || []} />
          <BulletinQualityCard q={data.bulletin_quality || {}} />
          <EvolutionCard series={evolution} />
          <AttritionCard a={data.attrition || {}} />
          <p className="px-1 pt-1 text-center text-[11px] text-muted-ink">Indicateurs calculés sur tes bulletins — horizon : {data?.label}.</p>
        </>
      )}
    </div>
  );
}
